import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { API_BASE_URL } from '../../utils/constants';
import { FaBriefcase, FaClock, FaDollarSign, FaMapMarkerAlt, FaUser, FaImage, FaCheckCircle, FaShare, FaBullhorn } from 'react-icons/fa';
import DashboardLayout from '../../components/layout/DashboardLayout';
import './CompleteProfileScreen.css';

const CompleteProfileScreen = () => {
  const { user, setUser } = useUser();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const accessToken = localStorage.getItem('ACCESS_TOKEN');
        const response = await fetch(`${API_BASE_URL}api/doctor/profile`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          setUser({
            ...user,
            ...data.doctor,
            name: data.doctor.fullName,
          });
        }
      } catch (error) {
        console.error('Error fetching profile data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const checklistItems = [
    {
      label: 'Specializations',
      description: 'Add or update your areas of expertise to attract the right clients.',
      completed: user?.speciality && user?.speciality.length > 0,
      icon: <FaBriefcase />,
      onClick: () => navigate('/manage-specializations'),
    },
    {
      label: 'Availability Hours',
      description: 'Set your availability to let clients know when they can book appointments.',
      completed: user?.availableSlots && user?.availableSlots?.length !== 0,
      icon: <FaClock />,
      onClick: () => navigate('/manage-opening-hours'),
    },
    {
      label: 'Consultation Fees',
      description: 'Update your consultation fees to ensure transparency with clients.',
      completed: user?.services && user?.services.length > 0,
      icon: <FaDollarSign />,
      onClick: () => navigate('/consultation-fees'),
    },
    {
      label: 'About & Experience',
      description: 'Edit your bio, education, and experience to build trust with clients.',
      completed: user?.education && user?.education.length > 0,
      icon: <FaUser />,
      onClick: () => navigate('/edit-profile'),
    },
    {
      label: 'Profile Picture',
      description: 'Upload a professional profile picture to make a great first impression.',
      completed: user && user.profilePicture,
      icon: <FaImage />,
      onClick: () => navigate('/update-profile-picture'),
    },
    {
      label: 'Location',
      description: 'Add your clinic or practice location to help clients find you easily.',
      completed: user?.location && user?.location.city,
      icon: <FaMapMarkerAlt />,
      onClick: () => navigate('/update-location'),
    },
  ];

  const completionPercentage = (checklistItems.filter(item => item.completed).length / checklistItems.length) * 100;

  const shareProfile = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My Professional Profile',
          text: `Check out my profile on MeetUrExpert: ${user?.profileLink}`,
          url: user?.profileLink,
        });
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      // Fallback for browsers that don't support Web Share API
      navigator.clipboard.writeText(`Check out my profile on MeetUrExpert: ${user?.profileLink}`);
      alert('Profile link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="complete-profile-loading">
        <div className="loading-spinner"></div>
        <p>Loading profile...</p>
      </div>
    );
  }

  return (
    <DashboardLayout title="Complete Profile" showBackButton={true}>
      <div className="complete-profile-container">
        <div className="profile-header">
          <h1 className="welcome-title">
            Welcome, {user?.name?.split(' ')[0]}! 👋
          </h1>
          {completionPercentage < 100 && (
            <p className="welcome-subtitle">
              Let's complete your profile to start offering your services and connect with clients.
            </p>
          )}
        </div>

        {/* Progress Section */}
        {completionPercentage < 100 && (
          <div className="progress-section">
            <div className="progress-bar-container">
              <div 
                className="progress-bar" 
                style={{ width: `${completionPercentage}%` }}
              ></div>
            </div>
            <p className="progress-text">
              {Math.round(completionPercentage)}% Profile Completed
            </p>
          </div>
        )}

        {/* Completion Celebration */}
        {completionPercentage === 100 && (
          <div className="completion-celebration">
            <div className="celebration-content">
              <FaCheckCircle className="celebration-icon" />
              <h2 className="celebration-title">
                🎉 Congratulations! Your profile is now complete.
              </h2>
              <p className="celebration-text">
                You can now start accepting appointments and connecting with clients.
              </p>
              <button 
                className="celebration-button"
                onClick={() => navigate('/appointments')}
              >
                Go to Appointments
              </button>
            </div>
          </div>
        )}

        {/* Checklist Items */}
        <div className="checklist-container">
          {checklistItems.map((item, index) => (
            <div
              key={index}
              className={`checklist-item ${item.completed ? 'completed' : 'pending'}`}
              onClick={item.onClick}
            >
              <div className="checklist-icon">
                {item.icon}
              </div>
              <div className="checklist-content">
                <h3 className="checklist-title">{item.label}</h3>
                <p className="checklist-description">{item.description}</p>
              </div>
              {item.completed && (
                <FaCheckCircle className="checklist-status" />
              )}
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        {completionPercentage === 100 && (
          <div className="action-buttons">
            <button className="action-button promote-button">
              <FaBullhorn />
              <span>Promote Your Profile</span>
            </button>
            <button className="action-button share-button" onClick={shareProfile}>
              <FaShare />
              <span>Share Your Profile</span>
            </button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default CompleteProfileScreen;